/**
 * IndieChatLeftSidebar
 * Left 220px pane: brand header · new conversation CTA · History/Prompts/Files tabs
 * Phase 2: real history from API + conversation selection
 */
import React, { useState } from 'react';
import { Plus, Search, MessageSquare, Loader2, X, Upload, Download, Trash2, FileText, Paperclip, Lock, Users } from 'lucide-react';
import {
  LeftTab,
  StarIcon,
  Prompt,
  m1Prompts as defaultM1Prompts,
  HistoryLockIcon,
  HistorySharedIcon,
} from './indie-chat.types';
import type { OrbitConversation } from '../../services/OrbitChatService';
import type { SaveUserPromptInput, UserPrompt } from '../../hooks/useUserPrompts';
import type { OrbitFile, OrbitFileUploadPolicy } from '../../hooks/useOrbitFiles';

export interface IndieChatLeftSidebarProps {
  activeTab: LeftTab;
  onTabChange: (t: LeftTab) => void;
  /** Phase 2: real conversation history */
  conversations?: OrbitConversation[];
  conversationsLoading?: boolean;
  onSelectConversation?: (conv: OrbitConversation) => void;
  onNewConversation?: () => void;
  /** Currently active conversation_id for highlight */
  activeConversationId?: string;
  /** Phase 3: DB-backed prompts (falls back to hardcoded if not provided) */
  m1Prompts?: Prompt[];
  /** Phase 3C: per-client prompts (only shown when non-empty) */
  clientPrompts?: Prompt[];
  /** Phase 3A: fires when user clicks a prompt — text is the prompt label */
  onPromptSelect?: (text: string) => void;
  /** Phase D: user-saved prompts */
  userPrompts?: UserPrompt[];
  onAddUserPrompt?: (input: string | SaveUserPromptInput) => Promise<void>;
  onDeleteUserPrompt?: (id: number) => Promise<void>;
  files?: OrbitFile[];
  filesLoading?: boolean;
  uploadingFile?: boolean;
  uploadPolicy?: OrbitFileUploadPolicy | null;
  onOpenFilePicker?: () => void;
  onDeleteFile?: (id: number) => Promise<void>;
  onDownloadFile?: (file: OrbitFile) => Promise<void>;
  onIncludeFileInContext?: (file: OrbitFile) => Promise<void>;
  contextFileIds?: number[];
  promptsDisabled?: boolean;
  historyDisabled?: boolean;
}

const TABS: LeftTab[] = ['history', 'prompts', 'files'];

/** Truncate a string to at most `n` chars with ellipsis */
function truncate(str: string | null | undefined, n: number): string {
  if (!str) return 'Untitled conversation';
  return str.length <= n ? str : str.slice(0, n - 1) + '…';
}

/** Format a UTC timestamp string as a relative label */
function relativeTime(ts: string | null | undefined): string {
  if (!ts) return '';
  const date = new Date(ts);
  if (isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 2) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function conversationTimestamp(conv: OrbitConversation): Date | null {
  const raw = conv.last_message_at ?? conv.created_at;
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function historyBucketLabel(conv: OrbitConversation): string {
  const stamp = conversationTimestamp(conv);
  if (!stamp) return 'Older';

  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  const startLastWeek = new Date(startToday);
  startLastWeek.setDate(startLastWeek.getDate() - 7);

  if (stamp >= startToday) return 'Today';
  if (stamp >= startYesterday) return 'Yesterday';
  if (stamp >= startLastWeek) return 'Last week';
  return 'Older';
}

const IndieChatLeftSidebar: React.FC<IndieChatLeftSidebarProps> = ({
  activeTab,
  onTabChange,
  conversations = [],
  conversationsLoading = false,
  onSelectConversation,
  onNewConversation,
  activeConversationId,
  m1Prompts = defaultM1Prompts,
  clientPrompts = [],
  onPromptSelect,
  userPrompts = [],
  onAddUserPrompt,
  onDeleteUserPrompt,
  files = [],
  filesLoading = false,
  uploadingFile = false,
  uploadPolicy = null,
  onOpenFilePicker,
  onDeleteFile,
  onDownloadFile,
  onIncludeFileInContext,
  contextFileIds = [],
  promptsDisabled = false,
  historyDisabled = false,
}) => {
  const [addingPrompt, setAddingPrompt] = useState(false);
  const [newPromptText, setNewPromptText] = useState('');
  const [saving, setSaving] = useState(false);
  const includedContextFileIds = new Set(contextFileIds);

  const handleAddSubmit = async () => {
    const trimmed = newPromptText.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onAddUserPrompt?.(trimmed);
      setNewPromptText('');
      setAddingPrompt(false);
    } finally {
      setSaving(false);
    }
  };

  const handleAddKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddSubmit(); }
    if (e.key === 'Escape') { setAddingPrompt(false); setNewPromptText(''); }
  };

  const uploadDisabled = Boolean(uploadingFile) || (uploadPolicy?.files_remaining_this_month ?? 1) <= 0;

  return (
    <div className="indie-chat flex flex-col w-[240px] flex-shrink-0 h-full border-r border-[#EAECF0] bg-white overflow-hidden">

      {/* Design-system header: search plus the existing live new-conversation action. */}
      <div className="flex items-center gap-1.5 p-3">
        <label className="flex h-7 flex-1 items-center gap-2 rounded-md border border-[#D0D5DD] bg-white px-3">
          <Search className="h-3.5 w-3.5 shrink-0 text-[#98A2B3]" aria-hidden />
          <input type="search" aria-label="Search conversations" placeholder="Search" className="w-full min-w-0 bg-transparent text-xs text-[#344054] placeholder:text-[#98A2B3] outline-none" />
        </label>
        <button
          onClick={onNewConversation}
          className="flex h-7 shrink-0 items-center gap-1 rounded-lg bg-[#6366F1] px-2.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
        >
          <Plus className="h-3 w-3" />
          New
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-[#EAECF0]">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => onTabChange(tab)}
            className={`flex-1 py-[9.5px] text-[11px] font-medium leading-[16.5px] tracking-[0.064px] capitalize transition-colors border-b-2 ${activeTab === tab
              ? 'text-[#101828] border-[#101828]'
              : 'text-[#98A2B3] border-transparent hover:text-[#667085]'
              }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto scrollbar-thin pt-1.5">

        {activeTab === 'prompts' && (
          <>
            {/* M1 Prompts */}
            {!promptsDisabled && (
              <div className="px-[10px] pt-1.5">
                <div className="px-1.5 py-[4.5px]">
                  <span className="text-[#98A2B3] text-[9px] font-semibold leading-[13.5px] tracking-[0.977px] uppercase">
                    M1 Prompts
                  </span>
                </div>
                {m1Prompts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onPromptSelect?.(p.label)}
                    className="w-full flex items-center gap-[7px] px-2 py-[6px] rounded-[8px] hover:bg-[#F4EBFF] active:bg-[#EDE9FE] transition-colors text-left group"
                  >
                    <div
                      className="flex-shrink-0 w-5 h-5 rounded-[4px] flex items-center justify-center"
                      style={{ background: p.iconBg }}
                    >
                      {p.icon}
                    </div>
                    <span className="text-[#344054] text-[11px] font-normal leading-[16.5px] tracking-[0.064px] line-clamp-1">
                      {p.label}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Client Prompts — only rendered when the DB has client-specific prompts */}
            {!promptsDisabled && clientPrompts.length > 0 && (
              <div className="px-[10px] pt-2">
                <div className="px-1.5 py-[4.5px]">
                  <span className="text-[#98A2B3] text-[9px] font-semibold leading-[13.5px] tracking-[0.977px] uppercase">
                    Client Prompts
                  </span>
                </div>
                {clientPrompts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onPromptSelect?.(p.label)}
                    className="w-full flex items-center gap-[7px] px-2 py-[6px] rounded-[8px] hover:bg-[#F4EBFF] active:bg-[#EDE9FE] transition-colors text-left group"
                  >
                    <div
                      className="flex-shrink-0 w-5 h-5 rounded-[4px] flex items-center justify-center"
                      style={{ background: p.iconBg }}
                    >
                      {p.icon}
                    </div>
                    <span className="text-[#344054] text-[11px] font-normal leading-[16.5px] tracking-[0.064px] line-clamp-1">
                      {p.label}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* My Prompts */}
            <div className="px-[10px] pt-2">
              <div className="flex items-center justify-between px-1.5 py-1">
                <span className="text-[#98A2B3] text-[9px] font-semibold leading-[13.5px] tracking-[0.977px] uppercase">
                  My Prompts
                </span>
                <button
                  onClick={() => { setAddingPrompt(true); setNewPromptText(''); }}
                  className="text-[#6941C6] text-[10px] font-semibold leading-[15px] tracking-[0.117px] hover:text-[#53389E]"
                >
                  + Add
                </button>
              </div>

              {addingPrompt && (
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <input
                    autoFocus
                    type="text"
                    value={newPromptText}
                    onChange={(e) => setNewPromptText(e.target.value)}
                    onKeyDown={handleAddKeyDown}
                    placeholder="Type a prompt…"
                    className="flex-1 min-w-0 text-[11px] text-[#344054] placeholder:text-[#98A2B3] border border-[#DDD6FE] rounded-[6px] px-2 py-1 outline-none focus:border-[#7F56D9] bg-white"
                  />
                  <button
                    onClick={handleAddSubmit}
                    disabled={!newPromptText.trim() || saving}
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-[5px] bg-[#7F56D9] hover:bg-[#6941C6] disabled:opacity-40 transition-colors"
                  >
                    {saving
                      ? <span className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin" />
                      : <Plus className="w-3 h-3 text-white" />
                    }
                  </button>
                  <button
                    onClick={() => { setAddingPrompt(false); setNewPromptText(''); }}
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-[5px] hover:bg-[#F9FAFB] transition-colors"
                  >
                    <X className="w-3 h-3 text-[#98A2B3]" />
                  </button>
                </div>
              )}

              {userPrompts.length === 0 && !addingPrompt && (
                <p className="px-2 py-2 text-[10px] text-[#C0C6D1] leading-[14px]">
                  No saved prompts — click <strong>+ Add</strong> to save a shortcut.
                </p>
              )}

              {userPrompts.map((p) => (
                <div
                  key={p.id}
                  className="w-full flex items-center gap-[7px] px-2 py-[6px] rounded-[8px] hover:bg-[#F4EBFF] transition-colors group"
                >
                  <button
                    type="button"
                    disabled={promptsDisabled}
                    onClick={() => onPromptSelect?.(p.prompt_text ?? p.label)}
                    className="flex-1 flex items-center gap-[7px] min-w-0 text-left disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <div className="flex-shrink-0 w-5 h-5 rounded-[4px] bg-[#F9F5FF] flex items-center justify-center">
                      <StarIcon />
                    </div>
                    <span className="text-[#344054] text-[11px] font-normal leading-[16.5px] tracking-[0.064px] flex-1 line-clamp-1">
                      {p.label}
                    </span>
                    <span className="flex-shrink-0 px-[5px] py-[1.5px] rounded-[5px] bg-[#F9F5FF] text-[#6941C6] text-[9px] font-semibold leading-[13.5px] tracking-[0.167px]">
                      Mine
                    </span>
                  </button>
                  <button
                    onClick={() => onDeleteUserPrompt?.(p.id)}
                    className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-[3px] opacity-0 group-hover:opacity-100 hover:bg-[#FEE4E2] transition-all"
                    title="Remove prompt"
                  >
                    <X className="w-2.5 h-2.5 text-[#D92D20]" />
                  </button>
                </div>
              ))}
              {promptsDisabled && (
                <p className="px-2 py-2 text-[10px] text-[#98A2B3] leading-[14px]">
                  Prompt shortcuts are unavailable while the Ask Indie maintenance banner is active.
                </p>
              )}
            </div>
          </>
        )}

        {activeTab === 'history' && (
          <div className="px-[10px] pt-1.5">
            {conversationsLoading && conversations.length === 0 ? (
              <div className="flex items-center justify-center py-8 gap-2 text-[#98A2B3]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-[11px]">Loading…</span>
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center gap-1 text-center py-8 px-2">
                <MessageSquare className="w-5 h-5 text-[#D0D5DD] mb-1" />
                <p className="text-[#98A2B3] text-[11px] font-medium">No history yet</p>
                <p className="text-[#C0C6D1] text-[10px] leading-[14px]">
                  Start a conversation and it will appear here automatically
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {['Today', 'Yesterday', 'Last week', 'Older'].map((bucket) => {
                  const items = conversations.filter((conv) => historyBucketLabel(conv) === bucket);
                  if (items.length === 0) return null;

                  return (
                    <div key={bucket}>
                      <p className="px-1.5 py-1 text-[#98A2B3] text-[9px] font-semibold leading-[13.5px] tracking-[0.977px] uppercase">
                        {bucket}
                      </p>
                      <div className="flex flex-col gap-0.5">
                        {items.map((conv) => {
                          const isActive = conv.conversation_id === activeConversationId;
                          const isShared = conv.conversation_visibility === 'shared';
                          return (
                            <div
                              key={conv.conversation_id}
                              className={`w-full flex items-center justify-between gap-1.5 px-2 py-[7px] rounded-[8px] transition-colors ${isActive
                                ? 'bg-[#F4EBFF]'
                                : 'hover:bg-[#F9FAFB]'
                                }`}
                            >
                              <button
                                type="button"
                                disabled={historyDisabled}
                                onClick={() => onSelectConversation?.(conv)}
                                className="flex-1 min-w-0 text-left disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <p className={`font-geist text-[14px] font-medium leading-[20px] line-clamp-1 ${isActive ? 'text-[#6941C6]' : 'text-[#344054]'}`}>
                                  {truncate(conv.first_user_message, 72)}
                                </p>
                                <p className="font-geist text-[12px] font-normal leading-[18px] text-[#667085] truncate mt-0.5">
                                  {isShared ? 'Shared with company' : 'Private conversation'}
                                </p>
                              </button>
                              <div
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${isShared ? 'bg-[#F0F9FF] text-[#0086C9]' : 'bg-[#F2F4F7] text-[#344054]'
                                  }`}
                              >
                                {isShared ? <HistorySharedIcon /> : <HistoryLockIcon />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {historyDisabled && conversations.length > 0 && (
              <p className="px-2 py-2 text-[10px] text-[#98A2B3] leading-[14px]">
                Conversation history cannot be opened while the Ask Indie maintenance banner is active.
              </p>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          <div className="px-[10px] pt-2 pb-3">
            <button
              onClick={onOpenFilePicker}
              disabled={uploadDisabled}
              title={uploadDisabled && uploadPolicy?.files_remaining_this_month === 0
                ? `${uploadPolicy.tier} tier monthly upload limit reached`
                : undefined}
              className={`w-full flex items-center justify-center gap-1.5 h-[32px] rounded-[8px] border text-[11px] font-semibold transition-colors ${uploadDisabled
                ? 'border-[#EAECF0] bg-[#F9FAFB] text-[#98A2B3] cursor-not-allowed'
                : 'border-[#DDD6FE] bg-[#F9F5FF] text-[#6941C6] hover:bg-[#F4EBFF]'}`}
            >
              <Upload className="w-3.5 h-3.5" />
              <span>{uploadingFile ? 'Uploading…' : 'Upload file'}</span>
            </button>

            {uploadPolicy && (
              <p className="mt-1.5 px-0.5 text-[9px] leading-[13px] text-[#98A2B3]">
                {uploadPolicy.tier} tier · {uploadPolicy.max_file_size_label}/file · {uploadPolicy.files_uploaded_this_month}/{uploadPolicy.max_files_per_month} uploaded this month
              </p>
            )}

            {filesLoading ? (
              <div className="flex items-center justify-center py-8 gap-2 text-[#98A2B3]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-[11px]">Loading files…</span>
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center gap-1 text-center py-8 px-2">
                <FileText className="w-5 h-5 text-[#D0D5DD] mb-1" />
                <p className="text-[#98A2B3] text-[11px] font-medium">No files uploaded yet</p>
                <p className="text-[#C0C6D1] text-[10px] leading-[14px]">
                  Upload CSV, JSON, PDF, or Excel files to reuse them in Indie AI.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5 mt-2">
                {files.map((file) => (
                  <div key={file.id} className="rounded-[10px] border border-[#EAECF0] bg-[#F9FAFB] px-2.5 py-2">
                    {(() => {
                      const alreadyIncluded = includedContextFileIds.has(file.id);
                      const contextStatus = file.context_status ?? 'unknown';
                      const includeDisabled = alreadyIncluded || contextStatus !== 'available';
                      const includeTitle = alreadyIncluded
                        ? 'Already included in current chat context'
                        : contextStatus === 'available'
                          ? 'Add file to current chat context'
                          : contextStatus === 'unknown'
                            ? 'Checking whether this file can be added to chat context…'
                            : file.context_status_message || 'This file cannot be added to chat context right now';

                      return (
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 w-7 h-7 rounded-[7px] bg-[#F4EBFF] flex items-center justify-center flex-shrink-0">
                            <FileText className="w-3.5 h-3.5 text-[#6941C6]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-[#344054] leading-[16px] line-clamp-2">
                              {file.original_filename}
                            </p>
                            <p className="text-[9px] text-[#98A2B3] mt-0.5">
                              {file.file_size_label} · {file.status}
                            </p>
                            {contextStatus !== 'available' && contextStatus !== 'unknown' && file.context_status_message && (
                              <p className="text-[9px] text-[#D92D20] mt-1 leading-[13px]">
                                {file.context_status_message}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={async () => {
                                if (!onIncludeFileInContext || includeDisabled) return;
                                const confirmed = window.confirm(`Include “${file.original_filename}” in the current chat context?`);
                                if (!confirmed) return;
                                await onIncludeFileInContext(file);
                              }}
                              className={`w-6 h-6 flex items-center justify-center rounded-[6px] transition-colors ${alreadyIncluded ? 'bg-[#F4EBFF]' : includeDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white'}`}
                              title={includeTitle}
                              aria-label={alreadyIncluded
                                ? `File ${file.original_filename} is already in the current chat context`
                                : includeDisabled
                                  ? `Cannot add ${file.original_filename} to the current chat context: ${includeTitle}`
                                  : `Add ${file.original_filename} to the current chat context`}
                              disabled={includeDisabled}
                            >
                              <Paperclip className={`w-3.5 h-3.5 ${alreadyIncluded ? 'text-[#6941C6]' : includeDisabled ? 'text-[#D0D5DD]' : 'text-[#98A2B3]'}`} />
                            </button>
                            <button
                              onClick={() => onDownloadFile?.(file)}
                              className="w-6 h-6 flex items-center justify-center rounded-[6px] hover:bg-white transition-colors"
                              title="Download file"
                            >
                              <Download className="w-3.5 h-3.5 text-[#98A2B3]" />
                            </button>
                            <button
                              onClick={() => onDeleteFile?.(file.id)}
                              className="w-6 h-6 flex items-center justify-center rounded-[6px] hover:bg-[#FEE4E2] transition-colors"
                              title="Delete file"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-[#D92D20]" />
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IndieChatLeftSidebar;
